import React, { useState } from "react";
import { Button, Alert } from "react-bootstrap";
import { Upload as UploadIcon } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";

import { useCertifications } from "../hooks/useCertifications";

const CertificationImportModal = ({
  onClose = () => {},
  onSuccess = () => {},
}) => {
  const { t } = useTranslation(["certification"]);
  const { bulkAddCertifications, downloadCertificationTemplate, loading } =
    useCertifications();

  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState([]);

  /* ---------- FILE CHANGE ---------- */
  const handleFileChange = (e) => {
    const file = e.target.files[0];

    const isExcel =
      file &&
      (file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel");

    if (isExcel) {
      setSelectedFile(file);
      setError("");
      setErrorDetails([]);
    } else {
      setSelectedFile(null);
      setError(t("certification:invalid_file"));
    }
  };

  /* ---------- UPLOAD ---------- */
  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t("certification:no_file_selected"));
      return;
    }

    const result = await bulkAddCertifications(selectedFile);

    if (result?.success) {
      onSuccess();
      onClose();
    } else {
      setError(result?.message || t("certification:import_error"));
      setErrorDetails(result?.details || []);
    }
  };

  return (
    <div>
      {/* ---------- UPLOAD CARD ---------- */}
      <div
        className="import-area p-4 rounded"
        style={{ background: "#fceee9" }}
      >
        <div className="text-center mb-3">
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 12,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
              marginBottom: "1rem",
            }}
          >
            <UploadIcon size={32} />
          </div>

          <h5 className="mb-2 uploadfile">
            {t("certification:upload_certifications")}
          </h5>

          <p className="text-muted small">{t("certification:support_xlsx")}</p>
        </div>

        {/* ---------- ERROR ---------- */}
        {error && (
          <Alert variant="danger">
            <div className="fw-semibold">{error}</div>

            {errorDetails.length > 0 && (
              <div
                className="mt-2"
                style={{ maxHeight: "150px", overflowY: "auto" }}
              >
                <ul className="mb-0">
                  {errorDetails.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
          </Alert>
        )}

        {/* ---------- FILE INPUT ---------- */}
        <input
          id="upload-xlsx-cert"
          type="file"
          accept=".xlsx,.xls"
          hidden
          onChange={handleFileChange}
          disabled={loading}
        />

        <div className="text-center mb-3">
          <label htmlFor="upload-xlsx-cert">
            <Button
              variant="primary"
              as="span"
              className="btnupload"
              disabled={loading}
            >
              {selectedFile
                ? t("certification:reupload_xlsx")
                : t("certification:upload_xlsx")}
            </Button>
          </label>

          {selectedFile && (
            <div className="mt-2">
              <small className="text-muted d-block">{selectedFile.name}</small>

              <Button
                variant="outline-danger"
                size="sm"
                className="mt-2"
                onClick={() => {
                  setSelectedFile(null);
                  setError("");
                  setErrorDetails([]);
                }}
                disabled={loading}
              >
                {t("certification:remove")}
              </Button>
            </div>
          )}
        </div>

        {/* ---------- TEMPLATE DOWNLOAD ---------- */}
        <div className="text-center mb-3 import-area small">
          {t("certification:download_template")}:
          <button
            type="button"
            onClick={downloadCertificationTemplate}
            className="btn btn-link p-0 text-primary text-decoration-none btnfont"
            style={{ cursor: "pointer" }}
            disabled={loading}
          >
            {" "}
            XLSX
          </button>
        </div>
      </div>

      {/* ---------- FOOTER ---------- */}
      <div className="d-flex justify-content-end gap-2 modal-footer-custom">
        <Button
          variant="outline-secondary"
          onClick={onClose}
          disabled={loading}
        >
          {t("certification:cancel")}
        </Button>

        <Button variant="primary" onClick={handleUpload} disabled={loading}>
          {loading ? t("certification:importing") : t("certification:import")}
        </Button>
      </div>
    </div>
  );
};

export default CertificationImportModal;
